import {Component, Inject, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Data} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {environment} from "@environment";
import {
  AbstractComponent,
  CompetitorTableType
} from "@components";
import {
  ActionResultList,
  ApplicationFormSettings,
  CategoryItem,
  Competitor,
  CompetitorFilter,
  CompetitorResults,
  Country,
  EventItem,
  EventStatus,
  Lookups, PagedFilter,
  Race, Pages,
  RaceSettings,
  TypeOfApplicationTypes,
} from "@model";
import {
  CompetitorResultsService, CompetitorService,
  EventService, PublicEventsService,
} from "@services";
import {DatePipe, DOCUMENT} from "@angular/common";
import {EventDetailsHolder} from "@resolvers";
import {Meta, Title} from '@angular/platform-browser';
import {getLanguageValue} from '@utility';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'app-event-details',
  templateUrl: './event-details.component.html',
  styleUrls: ['./event-details.component.scss'],
  providers: [DatePipe]
})
export class EventDetailsComponent extends AbstractComponent implements OnInit {
  @Input() eventDetailsHolder?: EventDetailsHolder;
  @Input('eventDetails') event?: EventItem;
  @Input() countries: Lookups[] = [];
  @Input() currencies: Lookups[] = [];
  @Input() genders: Lookups[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() results: CompetitorResults[] = [];
  @Input() categories: CategoryItem[] = [];
  @Input() competitors: Competitor[] = [];
  @Input() shared?: string;

  eventCountry?: Country;
  eventCurrency?: string;

  componentType: EventDetailsComponentType  = EventDetailsComponentType.DETAILS;

  lengths: (string | undefined)[] = [];
  applicationFormSettingsList: ApplicationFormSettings[] = [];
  settingsList: RaceSettings[] = [];
  hideCompetitors: boolean = true;
  competitorTableEvent?: EventItem;
  myUpcomingEvent: EventItem | undefined;
  myPastEvent: EventItem | undefined;
  races: Race[] = [];
  isMobile = false;

  constructor(
    private eventService: EventService,
    private publicEventService: PublicEventsService,
    public translocoService: TranslocoService,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private competitorService: CompetitorService,
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) public document: Document,
    private breakpointObserver: BreakpointObserver
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.eventDetailsHolder) {
      this.event = this.eventDetailsHolder.eventItem;
      this.results = this.eventDetailsHolder.results;
      this.categories = this.eventDetailsHolder.categories;
      if (this.shared === 'shared') {
        this.setSEOData();
      }
      this.breakpointObserver.observe([`(max-width: 991.98px)`]).subscribe(result => {
        this.isMobile = result.matches;
      });
    }

    this.subscribeToRouteDataChange();
    this.eventService.setCurrentEvent(this.event);
    this.setEventLengths();
    this.setEventCountry();
    this.setEventCurrency();
    this.applicationFormSettingsList = (this.event?.racesList?.flatMap(item => item.applicationFormSettings) ?? []).filter((item): item is ApplicationFormSettings => !!item);
    this.settingsList = (this.event?.racesList?.flatMap(item => item.raceSettings) ?? []).filter((item): item is RaceSettings => !!item);
    this.hideCompetitors = this.event?.status === EventStatus.FINISHED
      || this.competitors.length === 0
      || this.settingsList.every(setting => setting.typeOfApplication === TypeOfApplicationTypes.DO_NOT_DISPLAY_PARTICIPANT_LIST);
    if (!this.hideCompetitors) {
      this.competitorTableEvent = {...this.event};
      this.competitorTableEvent.racesList = [
        ...(this.competitorTableEvent.racesList?.filter(
          (race: Race) =>
            !race.raceSettings ||
            race.raceSettings.typeOfApplication !== TypeOfApplicationTypes.DO_NOT_DISPLAY_PARTICIPANT_LIST
        ) ?? [])
      ];
    }
  }

  private subscribeToRouteDataChange(): void {
    this.subscriptions.push(
      this.route.data.subscribe(({isPreview}: Data) => {
        if (isPreview) {
          this.componentType = EventDetailsComponentType.PREVIEW;
        }
      }),
        this.route.data.subscribe(({ isMyTracey }: Data) => {
          if (isMyTracey) {
            this.route.queryParams.subscribe(params => {
              const isFinished = params['isFinishedEvent'] === 'true';
              const isFavourite = params['isFavourite'] === 'true';
              if (!isFavourite) {
                if (isFinished) {
                  this.componentType = EventDetailsComponentType.MY_PAST;
                  this.setPastEvent(this.publicEventService.pastRegisteredEvents.value)
                }
                else {
                  this.componentType = EventDetailsComponentType.MY_UPCOMING;
                  this.setUpcomingEvent(this.publicEventService.upcomingRegisteredEvents.value)
                }
              }
            });
          }
        }),
      this.route.queryParams.subscribe(params => {
        if (params['isFavourite'] === 'true') {
          this.componentType = EventDetailsComponentType.MY_FAVOURITE
        }
      })
    );

    this.subscriptions.push(
        this.publicEventService.upcomingRegisteredEvents.subscribe((value: ActionResultList<EventItem[], number> | null) => this.setUpcomingEvent(value))
    );

    this.subscriptions.push(
        this.publicEventService.pastRegisteredEvents.subscribe((value: ActionResultList<EventItem[], number> | null) => this.setPastEvent(value))
    );
  }

  setEventLengths(): void {
    if (this.event?.lengths?.length) {
      // @ts-ignore
      this.lengths = this.eventItem.lengths.sort((a, b) => (a || 0) - (b || 0))
    } else {
      this.lengths = Array.from(new Set(this.event?.racesList?.map(race => race.length).sort((a, b) => (a || 0) - (b || 0)).map(length => length?.toString())));
    }
  }

  setUpcomingEvent(value: ActionResultList<EventItem[], number> | null): void {
    if (this.componentType === EventDetailsComponentType.MY_UPCOMING) {
      this.myUpcomingEvent = (value?.result ?? []).find(upcoming => upcoming.id === this.event?.id);

      if (this.myUpcomingEvent && this.event) {
        const races = this.event.racesList?.filter(race =>
            this.myUpcomingEvent?.racesList?.some(upcomingRace => upcomingRace.id === race.id)
        ) ?? [];

        this.event.racesList = [...races];
      }
      this.setCompetitors()

    }
  }

  setPastEvent(value: ActionResultList<EventItem[], number> | null): void {
    if (this.componentType === EventDetailsComponentType.MY_PAST) {
      this.myPastEvent = (value?.result ?? []).find(past => past.id === this.event?.id);

      if (this.myPastEvent && this.event) {
        const races = this.event.racesList?.filter(race =>
            this.myPastEvent?.racesList?.some(pastRace => pastRace.id === race.id)
        ) ?? [];

        this.event.racesList = [...races];
      }
      this.setCompetitors()
    }
  }

  setCompetitors() {
    if (this.event?.racesList?.at(0)) {
      const filter: CompetitorFilter = new CompetitorFilter();
      filter.eventId = this.event?.id;
      filter.raceId = this.event?.racesList?.at(0)?.id;
      this.competitorService.findCompetitors(
          new PagedFilter(new Pages(undefined, 1), filter), false, false).subscribe(value =>
      this.competitors = value
      );
    }
    else {
      this.competitors = [];
    }
  }


  private setEventCountry(): void {
    if (this.countries.length && this.event?.country) {
      this.eventCountry = this.countries.find(item => item.id === this.event?.country);
    }
  }

  private setEventCurrency(): void {
    this.eventCurrency = this.currencies.find(currency => currency.id === this.event?.currency)?.name ?? "";
  }

  formatDate(date: Date | string): string {
    return this.datePipe.transform(date, 'MMM', undefined, this.getLocale())?.slice(0, 3)?.toUpperCase() ?? '';
  }

  getLocale() {
    let activeLang = this.translocoService.getActiveLang();
    switch (activeLang) {
      case 'hr':
        return 'hr';
      case 'en':
        return 'en';
      case 'it':
        return 'it';
      case 'slo':
        return 'slo';
      case 'sr':
        return 'sr-Latn';
      case 'bs':
        return 'sr-Latn';
      default:
        return 'en';
    }
  }

  setSEOData(): void {
    if (this.event) {
      const title = getLanguageValue(
        this.event.name, this.event.nameEng, this.event.nameIta,
        this.event.nameSlo, this.event.nameSr, this.event.nameBs, this.translocoService.getActiveLang()
      ) ?? '';

      this.titleService.setTitle(title);

      var url = this.document.location.href.replace("/private", "");

      if (this.event.status === EventStatus.FINISHED) {
        url = url.replace("/profile/myTracey", "/finishedEvents");
      } else {
        url = url.replace("/profile/myTracey", "/eventCalendar");
      }
      this.metaService.updateTag({property: 'og:url', content: url});

      this.metaService.updateTag({property: 'og:title', content: title});
      this.metaService.updateTag({property: 'og:type', content: 'website'});
      this.metaService.updateTag({property: 'og:site_name', content: environment.site_name});
      this.metaService.updateTag({property: 'og:image', content: this.event?.imageMedium ?? ''});
    }
  }

  protected readonly environment = environment;
  protected readonly EventStatus = EventStatus;
  protected readonly EventDetailsComponentType = EventDetailsComponentType;
  protected readonly CompetitorTableType = CompetitorTableType;
}
export enum EventDetailsComponentType {
  DETAILS,
  PREVIEW,
  MY_UPCOMING,
  MY_PAST,
  MY_FAVOURITE
}



